/**
 * API Configuration for Frontend-Backend Integration
 *
 * Resolution order for each base URL:
 *   1. REACT_APP_* env var (baked at build time) — wins if non-empty.
 *   2. Production default: relative URL (same origin, behind nginx/ALB).
 *   3. Dev default: explicit localhost port.
 *
 * In production (behind nginx/ALB), all routes are on the same origin:
 *   /api/*  → chatbot-app
 *   /rust/* → rust-backend
 *   /ws/*   → chatbot-app (WebSocket)
 *
 * In Docker Compose dev, REACT_APP_CHATBOT_URL is set to
 * http://localhost:8000 so the production build still reaches the backend.
 */

/** Treat empty strings the same as undefined. */
const envOrDefault = (envValue: string | undefined, fallback: string): string =>
  envValue && envValue.trim() !== '' ? envValue.trim() : fallback;

const isProduction = process.env.NODE_ENV === 'production';
const hostname = window.location.hostname;
const isLocalhostRuntime = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

// In remote browser runtimes (Codespaces/app.github.dev), ignore env vars that point to localhost.
const normalizeBrowserBase = (base: string): string => {
  const lower = base.toLowerCase();
  const localhostPrefixes = [
    'http://localhost:',
    'https://localhost:',
    'http://127.0.0.1:',
    'https://127.0.0.1:',
    'http://[::1]:',
    'https://[::1]:',
  ];
  const pointsToLocalhost = localhostPrefixes.some((prefix) => lower.startsWith(prefix));
  return !isLocalhostRuntime && pointsToLocalhost ? '' : base;
};

export const chatbotBase = normalizeBrowserBase(
  envOrDefault(
    process.env.REACT_APP_CHATBOT_URL,
    isLocalhostRuntime && !isProduction ? 'http://localhost:8000' : '',
  ),
);

export const rustBase = normalizeBrowserBase(
  envOrDefault(
    process.env.REACT_APP_RUST_BACKEND_URL,
    isLocalhostRuntime && !isProduction ? 'http://localhost:3001' : '',
  ),
);

export const tradingBase = normalizeBrowserBase(
  envOrDefault(
    process.env.REACT_APP_TRADING_URL,
    isLocalhostRuntime && !isProduction ? 'http://localhost:3010' : '',
  ),
);

// Job monitor lives outside Compose (systemd on the host); nginx routes /jobmon.
export const jobMonitorBase = normalizeBrowserBase(
  envOrDefault(
    process.env.REACT_APP_JOB_MONITOR_URL,
    isLocalhostRuntime && !isProduction ? 'http://localhost:8010' : '',
  ),
);

// WebSocket: derive from chatbotBase when it's an absolute URL,
// otherwise fall back to current page origin with ws(s) protocol.
const wsBase = chatbotBase
  ? chatbotBase.replace(/^http/, 'ws')
  : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

const tradingWsUrl = envOrDefault(
  process.env.REACT_APP_TRADING_WS_URL,
  tradingBase
    ? tradingBase.replace(/^http/, 'ws') + '/ws/trading'
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/trading/ws/trading`,
);

const marketMonitorLiveWsUrl = tradingBase
  ? tradingBase.replace(/^http/, 'ws') + '/ws/market-monitor/stream'
  : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/trading/ws/market-monitor/stream`;

const config = {
  // Backend API base URL (with /api prefix for all API routes)
  apiUrl: chatbotBase + '/api',

  // Rust backend API base URL – in prod goes through nginx at /rust/api
  rustApiUrl: rustBase ? rustBase + '/api' : '/rust/api',

  // Trading backend API base URL – in prod expected at /trading/api through reverse proxy.
  tradingApiUrl: tradingBase ? tradingBase + '/api' : '/trading/api',

  // Job monitor API base URL – in prod goes through nginx at /jobmon/api/v1
  jobMonitorApiUrl: jobMonitorBase ? jobMonitorBase + '/api/v1' : '/jobmon/api/v1',

  // WebSocket URL - websockets are under /ws path
  websocketUrl: wsBase + '/ws/chat',

  // Trading websocket endpoint
  tradingWsUrl,

  // Market-monitor live ticking websocket endpoint
  marketMonitorLiveWsUrl,

  // API endpoints
  endpoints: {
    createSession: '/create_session',
    sessions: '/sessions',
    session: '/session',
    health: '/health',
  },
};

export default config;
